import { ModeComment } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { Types } from '../../../enums/TypeEnums';
import {
  IMembership,
  IProduct,
  useMembershipProductsLazyQuery,
  useUpdateMembershipMutation
} from '../../../generated/graphql';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import EpraDatePicker from '../../Common/Fields/EpraDatePicker';
import InputComponent from '../../Common/Fields/InputComponent';
import { ErrorTexts, IErrors } from '../NewMembership';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Actions } from '../../../enums/ActionEnums';
import { parseDate, sameMembershipNameExist } from '../../../utils/clientUtils';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import PaperCard from '../../Common/Layout/PaperCard';
import EditButton from '../../Common/Buttons/EditButton';
import CommentsList from '../../Comments/CommentsList';

type IProps = {
  membership: IMembership;
  updateCommentsCount: (isRemoved: boolean) => void;
};

const defaultErrorsObj: IErrors = {
  name: null,
  type: null,
  discount: null,
  startDate: null,
  renewalDate: null,
  endDate: null
};

const minDate = new Date('01.01.2000');
const startMinDateMessage = `date should be after ${minDate}`;
const renewalMinDateMessage = 'renewal date should be after start date';
const endMinDateMessage = 'end date should be after start date';
const endDateBeforeRenewalMessage = 'end date should be after renewal date';

const MembershipDetailsTab: React.FC<IProps> = ({ membership, updateCommentsCount }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [openComments, setOpenComments] = useState(false);
  const [getMembershipTypes, { data }] = useMembershipProductsLazyQuery();
  const [editable, setEditable] = useState(false);
  const [name, setName] = useState(membership.name);
  const [badDeptor, setBadDeptor] = useState(membership.is_bad_debtor);
  const [discount, setDiscount] = useState(membership.discount);
  const [type, setType] = useState<IProduct | null>(membership.product);
  const [options, setOptions] = useState([membership.product]);
  const [startDate, setStartDate] = useState<Date | null>(parseDate(membership.start_date));
  const [renewalDate, setRenewalDate] = useState<Date | null>(parseDate(membership.renewable_date));
  const [endDate, setEndDate] = useState<Date | null>(parseDate(membership.end_date));
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [updateMembership, updateMembershipResult] = useUpdateMembershipMutation();
  const canEdit = canUserEdit(state.user, Types.Membership);

  useEffect(() => {
    if (data && data.result) {
      setOptions(data.result as never[]);
    }
  }, [data?.result]);

  const submitForm = () => {
    const errors = {
      name: null,
      type: null,
      discount: null,
      startDate: null,
      renewalDate: null,
      endDate: null
    } as IErrors;

    let hasErrors = false;
    if (!name || name.length < 2) {
      hasErrors = true;
      errors.name = ErrorTexts.name;
    }
    if (!type) {
      hasErrors = true;
      errors.type = ErrorTexts.type;
    }
    const productPrice = type?.price as number;
    if (discount && (discount < 0 || discount > productPrice)) {
      hasErrors = true;
      errors.discount = ErrorTexts.discount;
    }
    if (!startDate) {
      hasErrors = true;
      errors.startDate = ErrorTexts.startDate;
    } else {
      if (!Date.parse(startDate.toString())) {
        hasErrors = true;
        errors.startDate = ErrorTexts.startDateInvalidDate;
      }
    }

    if (renewalDate) {
      if (Date.parse(renewalDate.toString())) {
        if (startDate && startDate > renewalDate) {
          hasErrors = true;
          errors.renewalDate = renewalMinDateMessage;
        }
      } else {
        hasErrors = true;
        errors.renewalDate = 'invalid renewal date';
      }
    }
    if (endDate) {
      if (Date.parse(endDate.toString())) {
        if (renewalDate && renewalDate > endDate) {
          hasErrors = true;
          errors.endDate = endDateBeforeRenewalMessage;
        }
        if (startDate && startDate > endDate) {
          hasErrors = true;
          errors.endDate = endMinDateMessage;
        }
      } else {
        hasErrors = true;
      }
    }
    if (!hasErrors && sameMembershipNameExist(name, membership.id)) {
      hasErrors = true;
      errors.name = ErrorTexts.nameExists;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      submitMembership();
    }
  };

  const submitMembership = () => {
    const variables = {
      id: parseInt(membership.id),
      input: {
        name,
        product_id: parseInt(type?.id as string),
        start_date: startDate ? startDate.toString() : '',
        end_date: endDate ? endDate.toString() : '',
        renewable_date: renewalDate ? renewalDate.toString() : '',
        discount: discount ? parseFloat(discount.toString()) : null,
        total: parseFloat(getTotalCost().toString()),
        is_bad_debtor: badDeptor
      }
    };
    updateMembership({
      variables,
      update(_, { data }) {
        if (data && data.result) {
          setEditable(false);
          dispatch({
            type: Actions.SetSelectedMembership,
            payload: {
              id: data.result.id,
              name: data.result.name,
              start_date: data.result.start_date ?? null,
              end_date: data.result.end_date ?? null
            }
          });
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Successfully updated ${data.result.name}`,
              severity: 'success',
              autoHide: 2000
            }
          });
        }
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          text: e.message,
          severity: 'error',
          autoHide: 6000
        }
      })
    );
  };

  const handleCancel = () => {
    setName(membership.name);
    setBadDeptor(membership.is_bad_debtor);
    setDiscount(membership.discount);
    setType(membership.product);
    setStartDate(parseDate(membership.start_date));
    setRenewalDate(parseDate(membership.renewable_date));
    setEndDate(parseDate(membership.end_date));
    setFormErrors(defaultErrorsObj);
    setEditable(false);
  };

  const getTotalCost = () => {
    return type ? type.price - Number(discount) : '';
  };

  const handleEditPressed = () => {
    getMembershipTypes();
    setEditable(true);
  };

  const handleMembershipTypeChange = (
    _: unknown,
    newValue: React.SetStateAction<IProduct | null>
  ) => {
    setDiscount(null);
    if (newValue) {
      const product = newValue as IProduct;
      ErrorTexts.discount = `discount should be between 0 and ${product.price}`;
      setFormErrors({ ...formErrors, type: null, discount: null });
    } else {
      ErrorTexts.discount = `discount should be bigger than 0`;
      setFormErrors({ ...formErrors, type: ErrorTexts.type, discount: null });
    }

    setType(newValue);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: ErrorTexts.name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
  };

  const handleDiscountChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    const disc = Number(value);
    const productPrice = type?.price as number;
    if ((disc && disc < 0) || disc > productPrice) {
      setFormErrors({
        ...formErrors,
        discount: type ? `discount should be between 0 and ${type.price}` : 'invalid discount'
      });
    } else {
      setFormErrors({ ...formErrors, discount: null });
    }
    setDiscount(isNaN(disc) ? null : disc);
  };

  const handleEndDateChange = (value: Date | null) => {
    if (value && startDate && value <= startDate) {
      setFormErrors({ ...formErrors, endDate: endMinDateMessage });
    } else if (value && renewalDate && value < renewalDate) {
      setFormErrors({ ...formErrors, endDate: endDateBeforeRenewalMessage });
    } else {
      setFormErrors({ ...formErrors, endDate: null });
    }
    setEndDate(value);
  };

  const handleRenewalDateChange = (value: Date | null) => {
    if (value) {
      const dateErrors: {
        renewalDate: string | null;
        endDate: string | null;
      } = {
        renewalDate: formErrors.renewalDate,
        endDate: formErrors.endDate
      };

      if (startDate && startDate > value) {
        dateErrors.renewalDate = renewalMinDateMessage;
      } else {
        dateErrors.renewalDate = null;
      }
      if (endDate && endDate < value) {
        dateErrors.endDate = endDateBeforeRenewalMessage;
      } else if (dateErrors.endDate === endDateBeforeRenewalMessage) {
        dateErrors.endDate = null;
      }
      setFormErrors({ ...formErrors, ...dateErrors });
    } else {
      setFormErrors({
        ...formErrors,
        renewalDate: null,
        endDate: formErrors.endDate === endDateBeforeRenewalMessage ? null : formErrors.endDate
      });
    }
    setRenewalDate(value);
  };

  const handleStartDateChange = (value: Date | null) => {
    if (value) {
      const dateErrors: {
        startDate: string | null;
        renewalDate: string | null;
        endDate: string | null;
      } = {
        startDate: null,
        renewalDate: formErrors.renewalDate,
        endDate: formErrors.endDate
      };

      if (value < new Date(minDate)) {
        dateErrors.startDate = startMinDateMessage;
      } else if (dateErrors.startDate === startMinDateMessage) {
        dateErrors.startDate = null;
      }
      if (endDate && value >= endDate) {
        dateErrors.endDate = endMinDateMessage;
      } else if (
        endDate &&
        dateErrors.endDate &&
        (dateErrors.endDate as string) === endMinDateMessage &&
        value < endDate
      ) {
        dateErrors.endDate = null;
      }
      if (renewalDate && renewalDate < value) {
        dateErrors.renewalDate = renewalMinDateMessage;
      } else {
        dateErrors.renewalDate = null;
      }
      setFormErrors({ ...formErrors, ...dateErrors });
    } else {
      setFormErrors({ ...formErrors, startDate: ErrorTexts.startDate });
    }
    setStartDate(value);
  };

  return (
    <Box className="centralize" sx={{ width: '100%', paddingTop: '80px' }}>
      <PaperCard width="82%" headerText="Details" additionalStyle={{ height: '680px' }}>
        <Box sx={{ textAlign: 'right', width: '100%', marginTop: '28px' }}>
          <EditButton
            id="membership-details-edit-button"
            onClick={handleEditPressed}
            disabled={!canEdit || editable}
            editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: '48px 100px',
            paddingX: '20%',
            paddingBottom: '210px',
            gridAutoRows: '36px'
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <InputComponent
              id="membership-details-name"
              label="Membership Name"
              data-testid="membership-details-name-input-label"
              value={name}
              onChange={handleNameChange}
              disabled={!editable || updateMembershipResult.loading}
              errorMsg={formErrors.name}
              additionalStyle={{ gridColumn: 'span 3' }}
            />
            <AutocompleteComponent
              id="membership-details-type-autocomplete"
              data-testid="membership-details-type-autocomplete"
              disabled={!editable || updateMembershipResult.loading}
              label="Type"
              options={(options as never[]) || []}
              getOptionLabel={(option: IProduct) => option?.membership_type?.name as string}
              values={type}
              onChange={handleMembershipTypeChange}
              errorMsg={formErrors.type}
              additionalStyle={{ gridColumn: 'span 2' }}
            />
            <InputComponent
              id="membership-details-price"
              label="Price"
              value={type?.price ?? ''}
              disabled={true}
              data-testid="membership-details-price-input-label"
              additionalStyle={{ gridColumn: 'span 3' }}
            />
            <EpraDatePicker
              id="membership-details-start-date"
              label="Date Started"
              disabled={!editable || updateMembershipResult.loading}
              value={startDate}
              onChange={handleStartDateChange}
              minDate={minDate}
              errorMsg={formErrors.startDate}
              sx={{ gridColumn: 'span 2' }}
            />
            <InputComponent
              id="membership-details-discount"
              data-testid="membership-details-discount-input-label"
              label="Discount"
              onChange={handleDiscountChange}
              value={discount || ''}
              disabled={!editable || updateMembershipResult.loading}
              additionalStyle={{ gridColumn: 'span 3' }}
              errorMsg={formErrors.discount ?? undefined}
            />
            <EpraDatePicker
              id="membership-details-renewal-date"
              label="Date Renewal"
              value={renewalDate}
              onChange={handleRenewalDateChange}
              minDate={startDate || minDate}
              errorMsg={formErrors.renewalDate ?? undefined}
              disabled={!editable || updateMembershipResult.loading}
              sx={{ gridColumn: 'span 2' }}
            />
            <InputComponent
              id="membership-details-total"
              data-testid="membership-details-total-input-label"
              label="Total"
              value={getTotalCost()}
              onChange={() => {
                true;
              }}
              disabled={true}
              additionalStyle={{ gridColumn: 'span 3' }}
            />
            <EpraDatePicker
              id="membership-details-end-date"
              label="Date End"
              value={endDate}
              onChange={handleEndDateChange}
              minDate={renewalDate ? renewalDate : startDate ? startDate : minDate}
              errorMsg={formErrors.endDate ?? undefined}
              disabled={!editable || updateMembershipResult.loading}
              sx={{ gridColumn: 'span 2' }}
            />
            <Box sx={{ gridColumn: 'span 3' }}>
              <FormControl
                component="fieldset"
                disabled={!editable || updateMembershipResult.loading}
              >
                <RadioGroup
                  row
                  aria-label="position"
                  name="membreship-details-bad-debptor"
                  data-testid="membership-details-bad-debptor"
                  value={badDeptor}
                  onChange={(e) => setBadDeptor(e.currentTarget.value === 'true')}
                >
                  <FormLabel
                    data-testid="company-index"
                    sx={{
                      marginTop: '13px',
                      marginRight: '40px',
                      color: editable ? Colors.main : Colors.gray
                    }}
                    component="legend"
                  >
                    Bad Deptor
                  </FormLabel>
                  <FormControlLabel
                    value={true}
                    control={<Radio color="primary" />}
                    label="Yes"
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio color="primary" />}
                    label="No"
                    labelPlacement="end"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box sx={{ gridColumn: 'span 2' }}>
              <Button onClick={() => setOpenComments(true)}>
                <Badge badgeContent={membership.comments_count} color="primary" showZero>
                  <ModeComment style={{ color: Colors.skyBlue }} />
                </Badge>
                <Box
                  sx={{
                    paddingLeft: '10%',
                    color: Colors.main
                  }}
                >
                  comments
                </Box>
              </Button>
            </Box>
            {editable && (
              <Box className="centralize" sx={{ gridColumn: 'span 5' }}>
                <CancelButton
                  id="cancel-button"
                  disabled={updateMembershipResult.loading}
                  onClick={handleCancel}
                  text="Cancel"
                />
                <SaveButton
                  id="save-button"
                  disabled={updateMembershipResult.loading}
                  onClick={submitForm}
                  text="Save"
                />
              </Box>
            )}
          </LocalizationProvider>
        </Box>
        {openComments && (
          <CommentsList
            handleClose={() => setOpenComments(false)}
            membership={membership}
            updateCommentsCount={updateCommentsCount}
          />
        )}
      </PaperCard>
    </Box>
  );
};

export default MembershipDetailsTab;
